import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { MAINTENANCE_MESSAGE, isMaintenanceStatus, toFriendlyErrorMessage } from "../../../utils/network";
import ScoreBanner from "./ScoreBanner";

const serverUrl = process.env.REACT_APP_SERVER_HOST;
const TOKEN_KEY = "auth_token";

const buildHeaders = (withJson = false) => {
  const token = sessionStorage.getItem(TOKEN_KEY);
  if (!token) return null;
  const headers = { Authorization: `Bearer ${token}` };
  if (withJson) headers["Content-Type"] = "application/json";
  return headers;
};

const normalizeQuestionData = (item) => {
  if (!item || typeof item !== "object") return null;

  const questionId = Number(item.questionId);
  if (!Number.isFinite(questionId)) return null;

  const multipleChoice = Boolean(item.multipleChoice);
  const options = Array.isArray(item.options)
    ? item.options.filter((option) => typeof option === "string")
    : [];

  return {
    questionId,
    question: typeof item.question === "string" ? item.question : `Question ${questionId}`,
    multipleChoice,
    options,
  };
};

const normalizeQuestionGroupData = (item, index) => {
  if (!item || typeof item !== "object") return null;

  const questionGroupId = Number(item.questionGroupId);
  const questions = Array.isArray(item.questions)
    ? item.questions.map(normalizeQuestionData).filter(Boolean)
    : [];

  return {
    questionGroupId: Number.isFinite(questionGroupId) ? questionGroupId : index + 1,
    title: typeof item.title === "string" && item.title.trim() ? item.title : `Section ${index + 1}`,
    groupText: typeof item.groupText === "string" ? item.groupText : "",
    instructionText: typeof item.instructionText === "string" ? item.instructionText : "",
    groupType: typeof item.groupType === "string" ? item.groupType : "",
    questions,
  };
};

const normalizeSubmitResponse = (item) => {
  if (!item || typeof item !== "object") return null;

  return {
    testId: Number(item.testId),
    testName: typeof item.testName === "string" ? item.testName : "",
    durationMinutes: Number(item.durationMinutes) || 0,
    attemptNumber: Number(item.attemptNumber) || 1,
    startedAt: typeof item.startedAt === "string" ? item.startedAt : "",
    submittedAt: typeof item.submittedAt === "string" ? item.submittedAt : "",
    totalQuestions: Number(item.totalQuestions) || 0,
    answeredQuestions: Number(item.answeredQuestions) || 0,
    correctAnswers: Number(item.correctAnswers) || 0,
    scorePercentage: Number(item.scorePercentage) || 0,
    message: typeof item.message === "string" ? item.message : "Test submitted successfully!",
  };
};

const ProgressBar = ({ answered, total }) => {
  const percentage = total === 0 ? 0 : Math.round((answered / total) * 100);
  return (
    <div style={{ marginBottom: "24px" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          fontSize: "13px",
          color: "#4b5563",
          marginBottom: "6px",
        }}
      >
        <span>Progress</span>
        <span>{answered} / {total} answered</span>
      </div>
      <div style={{ height: "8px", background: "#e5e7eb", borderRadius: "999px", overflow: "hidden" }}>
        <div
          style={{
            height: "100%",
            width: `${percentage}%`,
            background: percentage === 100 ? "#16a34a" : "#3b82f6",
            borderRadius: "999px",
            transition: "width 0.2s ease",
          }}
        />
      </div>
    </div>
  );
};

const TestPage = () => {
  const { testId } = useParams();
  const { state } = useLocation();
  const navigate = useNavigate();

  const topicName = state?.topicName || `Test ${testId}`;
  const subject = (state?.subject || "").toLowerCase();
  const totalTime = state?.totalTime || 30; // minutes
  const subjectColour = subject === "maths" || subject === "math" ? "#1a56db" : "#16794a";

  const [questionGroups, setQuestionGroups] = useState([]);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [timeLeft, setTimeLeft] = useState(totalTime * 60);
  const [submittedResult, setSubmittedResult] = useState(null);

  const flattenedQuestions = useMemo(
    () => questionGroups.flatMap((group) => group.questions),
    [questionGroups]
  );

  const answeredCount = useMemo(
    () => flattenedQuestions.filter((q) => answers[q.questionId]?.trim()).length,
    [flattenedQuestions, answers]
  );

  const allAnswered = flattenedQuestions.length > 0 && answeredCount === flattenedQuestions.length;

  const fetchQuestionGroups = useCallback(async () => {
    const headers = buildHeaders();
    if (!headers) {
      navigate("/login");
      return;
    }

    try {
      const response = await fetch(`${serverUrl}/app/tests/${testId}/questions`, { headers });

      if (response.status === 401) {
        sessionStorage.removeItem(TOKEN_KEY);
        navigate("/login");
        return;
      }

      if (isMaintenanceStatus(response.status)) {
        throw new Error(MAINTENANCE_MESSAGE);
      }

      if (!response.ok) {
        throw new Error(`Failed to load questions (${response.status})`);
      }

      const wrapper = await response.json();
      if (!wrapper || typeof wrapper !== 'object') {
        throw new Error("Unexpected questions response from server");
      }

      const { durationMinutes, questions } = wrapper;
      if (!Array.isArray(questions)) {
        throw new Error("Unexpected questions response from server");
      }

      const groups = questions.map((group, index) => normalizeQuestionGroupData(group, index)).filter(Boolean);
      setQuestionGroups(groups);

      const initialAnswers = {};
      groups.forEach((group) => {
        group.questions.forEach((question) => {
          initialAnswers[question.questionId] = "";
        });
      });
      setAnswers(initialAnswers);
    } catch (err) {
      setError(toFriendlyErrorMessage(err, "Unable to load questions right now. Please try again."));
    } finally {
      setLoading(false);
    }
  }, [navigate, testId]);

  useEffect(() => {
    fetchQuestionGroups();
  }, [fetchQuestionGroups]);

  useEffect(() => {
    let timer;
    if (loading || submitting) return;

    if (timeLeft > 0) {
      timer = setInterval(() => {
        setTimeLeft((prev) => prev - 1);
      }, 1000);
    } else {
      handleSubmit();
    }

    return () => clearInterval(timer);
  }, [loading, submitting, timeLeft]);

useEffect(() => {
  if (submittedResult) {
    window.scrollTo(0, 0);
  }
}, [submittedResult]);

  const handleTextChange = (questionId, value) => {
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
  };

  const handleOptionSelect = (questionId, option) => {
    setAnswers((prev) => ({ ...prev, [questionId]: option }));
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);

    const headers = buildHeaders(true);
    if (!headers) {
      navigate("/login");
      return;
    }

    try {
      const payload = {
        answers: flattenedQuestions.map((question) => ({
          questionId: Number(question.questionId),
          answerText: (answers[question.questionId] || "").trim(),
        })),
      };

      const response = await fetch(`${serverUrl}/app/tests/${testId}/submit`, {
        method: "POST",
        headers,
        body: JSON.stringify(payload),
      });

      if (response.status === 401) {
        sessionStorage.removeItem(TOKEN_KEY);
        navigate("/login");
        return;
      }

      if (isMaintenanceStatus(response.status)) {
        throw new Error(MAINTENANCE_MESSAGE);
      }

      if (!response.ok) {
        throw new Error(`Submission failed (${response.status})`);
      }

      const rawResult = await response.json();
      const result = normalizeSubmitResponse(rawResult);
      if (!result || !Number.isFinite(result.testId)) {
        throw new Error("Unexpected submit response from server");
      }

      // Add user answers to the result
      result.questions = flattenedQuestions.map((q) => ({
        questionId: q.questionId,
        question: q.question,
        userAnswer: (answers[q.questionId] || "").trim(),
      }));

      setSubmittedResult(result);
      // navigate("/dashboard", {
      //   state: { successResult: result },
      // });
    } catch (err) {
      setError(toFriendlyErrorMessage(err, "Unable to submit test right now. Please try again."));
      setSubmitting(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontFamily: "Arial, sans-serif",
          color: "#666",
        }}
      >
        Loading questions...
      </div>
    );
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#f8fafc",
        padding: "0 16px 60px",
        fontFamily: "Arial, sans-serif",
      }}
    >
      <div
        style={{
          position: "sticky",
          top: 0,
          background: "#fff",
          borderBottom: "1px solid #e5e7eb",
          zIndex: 10,
          padding: "16px 0",
          marginBottom: "26px",
        }}
      >
        <div
          style={{
            maxWidth: "760px",
            margin: "0 auto",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <button
            onClick={() => navigate(-1)}
            style={{
              background: "#9ca3af",
              border: "none",
              padding: "7px 16px",
              borderRadius: "6px",
              cursor: "pointer",
              color: "#fff",
              fontSize: "14px",
              fontWeight: 600,
            }}
          >
            {'<-'} Back
          </button>
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <span
              style={{
                fontSize: "16px",
                fontWeight: 600,
                color: timeLeft < 300 ? "#dc3545" : "#374151",
              }}
            >
              ⏱️ {formatTime(timeLeft)}
            </span>
            <span
              style={{
                background: subjectColour,
                color: "#fff",
                padding: "3px 12px",
                borderRadius: "999px",
                fontSize: "12px",
                fontWeight: 600,
                textTransform: "capitalize",
                letterSpacing: "0.5px",
              }}
            >
              {state?.subject || "Test"}
            </span>
          </div>
        </div>
      </div>

      <div style={{ maxWidth: "760px", margin: "0 auto" }}>
        {submittedResult ? (
          <>
            <ScoreBanner result={submittedResult} />
            <div style={{ marginTop: "20px", textAlign: "center" }}>
              <button
                onClick={() => navigate(`/test-review/${testId}`, { state: { result: submittedResult, attemptId: submittedResult.attemptNumber } })}
                style={{
                  padding: "12px 24px",
                  background: "#007bff",
                  color: "#fff",
                  border: "none",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontSize: "16px",
                  fontWeight: 600,
                  marginRight: "10px",
                }}
              >
                View Detailed Results
              </button>
              <button
                onClick={() => navigate("/dashboard")}
                style={{
                  padding: "12px 24px",
                  background: "#28a745",
                  color: "#fff",
                  border: "none",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontSize: "16px",
                  fontWeight: 600,
                }}
              >
                OK
              </button>
            </div>
          </>
        ) : (
          <>
            <h2 style={{ fontSize: "24px", fontWeight: 800, color: "#111827", marginBottom: "4px" }}>
              {topicName}
            </h2>
            <p style={{ color: "#6b7280", fontSize: "14px", marginBottom: "20px" }}>
              Fill all answers across all sections to submit.
            </p>

            <ProgressBar answered={answeredCount} total={flattenedQuestions.length} />

            {error && (
              <div
                style={{
                  padding: "14px 18px",
                  background: "#fee2e2",
                  color: "#991b1b",
                  borderRadius: "8px",
                  marginBottom: "20px",
                  border: "1px solid #fca5a5",
                }}
              >
                {error}
              </div>
            )}

            {questionGroups.length === 0 ? (
              <p style={{ color: "#9ca3af" }}>No questions found for this test.</p>
            ) : (
              <>
                  {questionGroups.map((group) => (
                  <section
                    key={group.questionGroupId}
                    style={{
                      marginBottom: "16px",
                    }}
                  >
                    {group.groupType && (
                      <div style={{ marginBottom: "8px", fontSize: "12px", color: "#6b7280" }}>
                        Type: {group.groupType}
                      </div>
                    )}
                    {group.groupText && (
                      <p style={{ margin: "0 0 8px", color: "#374151", lineHeight: 1.5 }}>
                        {group.groupText}
                      </p>
                    )}
                    {group.instructionText && (
                      <p style={{ margin: "0 0 14px", color: "#4b5563", fontStyle: "italic" }}>
                        {group.instructionText}
                      </p>
                    )}

                    {group.questions.map((question, idx) => {
                      const isAnswered = Boolean(answers[question.questionId]?.trim());
                      const displayIndex = flattenedQuestions.findIndex((q) => q.questionId === question.questionId) + 1;

                      return (
                        <div
                          key={question.questionId}
                          style={{
                            border: `1px solid ${isAnswered ? "#86efac" : "#e5e7eb"}`,
                            borderLeft: `4px solid ${isAnswered ? "#16a34a" : subjectColour}`,
                            borderRadius: "10px",
                            padding: "14px 16px",
                            marginBottom: idx === group.questions.length - 1 ? 0 : "12px",
                            background: "#fff",
                          }}
                        >
                          <div style={{ fontWeight: 700, color: "#1f2937", marginBottom: "10px" }}>
                            {displayIndex}. {question.question}
                          </div>

                          {question.multipleChoice ? (
                            <div style={{ display: "grid", gap: "8px" }}>
                              {question.options.map((option) => {
                                const selected = answers[question.questionId] === option;
                                return (
                                  <label
                                    key={option}
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "8px",
                                      border: `1px solid ${selected ? subjectColour : "#d1d5db"}`,
                                      background: selected ? "#eff6ff" : "#f9fafb",
                                      borderRadius: "7px",
                                      padding: "8px 10px",
                                      cursor: "pointer",
                                    }}
                                  >
                                    <input
                                      type="radio"
                                      name={`q-${question.questionId}`}
                                      value={option}
                                      checked={selected}
                                      onChange={() => handleOptionSelect(question.questionId, option)}
                                      style={{ accentColor: subjectColour }}
                                    />
                                    <span>{option}</span>
                                  </label>
                                );
                              })}
                            </div>
                          ) : (
                            <input
                              type="text"
                              value={answers[question.questionId] || ""}
                              onChange={(e) => handleTextChange(question.questionId, e.target.value)}
                              placeholder="Type your answer here..."
                              style={{
                                width: "100%",
                                boxSizing: "border-box",
                                borderRadius: "7px",
                                border: `2px solid ${isAnswered ? "#86efac" : "#d1d5db"}`,
                                padding: "10px 12px",
                                outline: "none",
                              }}
                            />
                          )}
                        </div>
                      );
                    })}
                  </section>
                ))}

                <div
                  style={{
                    marginTop: "24px",
                    display: "flex",
                    justifyContent: "flex-end",
                    alignItems: "center",
                    gap: "12px",
                  }}
                >
                  {!allAnswered && (
                    <span style={{ fontSize: "13px", color: "#9ca3af" }}>
                      {flattenedQuestions.length - answeredCount} answer
                      {flattenedQuestions.length - answeredCount !== 1 ? "s" : ""} remaining
                    </span>
                  )}

                  <button
                    onClick={handleSubmit}
                    disabled={!allAnswered || submitting}
                    style={{
                      padding: "12px 34px",
                      background: allAnswered ? subjectColour : "#9ca3af",
                      color: "#fff",
                      border: "none",
                      borderRadius: "8px",
                      cursor: allAnswered && !submitting ? "pointer" : "not-allowed",
                      fontSize: "15px",
                      fontWeight: 700,
                    }}
                  >
                    {submitting ? "Submitting..." : "Submit Test"}
                  </button>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default TestPage;

